import { useEffect, useState, useRef, useMemo } from "react";
import "../styles/gis/gis.scss";
import MenuTab from "../components/menu_tab";
import GISHeader from "../components/gis/gisheader";
import myData from "../assets/data/data";
import {
  FaLayerGroup,
  FaTimes,
  FaMapMarkerAlt,
  FaMap,
  FaExpand,
  FaCompress,
  FaChartBar,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import Icon from 'ol/style/Icon';
import northarrow from "../assets/icons/north-arrow.png"

import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import MultiPolygon from "ol/geom/MultiPolygon";
import MultiLineString from "ol/geom/MultiLineString";
import { fromLonLat, toLonLat } from "ol/proj";
import { XYZ } from "ol/source";
import { ZoomToExtent, ScaleLine, defaults as defaultControls } from "ol/control";
import LoadingWidget from "../components/loading";
import { Style, Circle as CircleStyle, Fill, Stroke } from "ol/style";
import Overlay from "ol/Overlay";
import Legend from "../components/gis/legend";
import SearchBar from "../components/searchbar";
import { locateUser } from "../components/gis/locateUser";
import html2canvas from "html2canvas";
import { getBoreholeSVG, getBulkMeterSVG, getColoredMeterSVG, getControlValveSVG, getOfftakeSVG, getTankSVG, getTreatmentWorksSVG, getWeirSVG } from "../assets/icons/legendicons/mlegendicons";
import TabularData from "../components/tabular_data";
import { getLayerSchema, buildClassification, SHOW_ALL_KEY, getShowAllClass } from "../components/gis/attributeAnalyzer";

// Free point-elevation lookups — no API key for either. Both are public
// demo instances (rate-limited, occasionally unreachable — "Failed to
// fetch" from either one is that instance being down/CORS-blocked, not a
// bug here), so the second is tried only if the first fails outright.
// Self-host one (https://github.com/Jorl17/open-elevation, or
// https://www.opentopodata.org's own Docker image) for production use.
const ELEVATION_PROVIDERS = [
  (lat, lon) => ({
    url: `https://api.open-elevation.com/api/v1/lookup?locations=${lat},${lon}`,
    parse: (json) => json?.results?.[0]?.elevation,
  }),
  (lat, lon) => ({
    url: `https://api.opentopodata.org/v1/srtm90m?locations=${lat},${lon}`,
    parse: (json) => json?.results?.[0]?.elevation,
  }),
];

// Matches the same constant in ../components/searchbar — kept in sync
// manually since this is the one string both files need to agree on for
// the "All layers" option to round-trip through the lifted `searchScope`
// state below.
const ALL_LAYERS_KEY = "__all__";

// Turns a camelCase/snake_case field name into a readable label for the
// insights panel's generic "By: <attribute>" breakdown (e.g. "walkRoute"
// -> "Walk route").
const humanizeField = (field) => {
  const spaced = field.replace(/_/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Drops undefined/null/empty entries from a properties object — used so a
// field that's absent (auth-gated, or just not captured for this row)
// disappears from the popup instead of rendering as "undefined".
const compact = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

const formatCapturedOn = (value) => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

// dataviz reference palette (see palette.md) — status roles + fixed
// categorical order, reused for every stat/chip/bar drawn in the insights
// panel below.
const STATUS_COLOR = { good: "#0ca30c", warning: "#fab219", serious: "#ec835a", critical: "#d03b3b" };
const CATEGORICAL = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];
const SEQUENTIAL_BLUE = "#2a78d6";

// Legend swatch colors pinned to specific class labels, regardless of
// which attribute/layer they turn up under or which function produced the
// classification (attributeAnalyzer.js's buildClassification for most
// fields, or the manual walkRoute classification above) — this is a final
// pass applied to every layer's classes in layerClassifications below.
// Match is case-insensitive and exact on the class label; add more entries
// here for any other class that should always render a specific color.
const CLASS_COLOR_OVERRIDES = {
  "active": "#1560bd",
  "working (good condition)": "#1560bd",
};

function applyClassColorOverrides(classes) {
  return classes.map((cls) => {
    const override = CLASS_COLOR_OVERRIDES[String(cls.label).trim().toLowerCase()];
    return override ? { ...cls, color: override } : cls;
  });
}

export default function GISCustomers() {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [map, setMap] = useState(null);
  const [basemapSelected, setBasemapSelected] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [basemapsVisible, setBasemapsVisible] = useState(true);
  const [legendCollapsed, setLegendCollapsed] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // The collapsible Insights panel now derives everything from `layers`
  // client-side (see insightsData below) — no separate fetch, so it stays
  // in sync with every search and legend filter automatically.
  const [insightsCollapsed, setInsightsCollapsed] = useState(false);

  // Which layer the search bar's scope dropdown is set to — lifted up here
  // (rather than kept local to <SearchBar>) so Insights can show detail for
  // whichever layer the user is actually searching/filtering right now.
  const [searchScope, setSearchScope] = useState(ALL_LAYERS_KEY);

  const [layers, setLayers] = useState({
    Wards: {
      url: "/api/wardsserved",
      data: [],
      visibility: true,
      activeAttribute: null,   // auto-selected once data loads
      classFilters: {},
    },
    "Billing Routes": {
      url: "/api/billingroutes",
      data: [],
      visibility: false,
      activeAttribute: null,
      classFilters: {},
    },
    SupplyLines: {
      url: "/api/latestsupplylines",
      data: [],
      visibility: false,
      activeAttribute: null,
      classFilters: {},
    },
    "Customer Meters": {
      // Every account, every status — the "/active" endpoint filters
      // server-side (WHERE account_status ILIKE 'active'), which is why
      // the Legend and map canvas only ever showed Active accounts and the
      // "by account status" filter had nothing else to offer. Filtering
      // by status now happens client-side, in the Legend, like every
      // other attribute.
      url: "/api/latestcustomeraccounts",
      data: [],
      visibility: true,
      activeAttribute: null,
      classFilters: {},
    },
  });

  const [selectedFeature, setSelectedFeature] = useState({ layerName: null, id: null });

  const mapElement = useRef();
  const mapWrapperRef = useRef();
  const popupElement = useRef();
  const popupOverlay = useRef();

  // Coordinate -> elevation lookups get cached (rounded to ~11m precision)
  // so re-clicking the same spot, or the same tile at different zooms,
  // doesn't refire the request.
  //
  // Plain object, not `new Map()`: this file imports OpenLayers' default
  // export as `Map` ("ol/Map"), which shadows the built-in Map constructor
  // for the rest of the module — `new Map()` here would silently construct
  // an ol.Map instance instead, which has no .has()/.get()/.set(), hence
  // "elevationCache.current.has is not a function".
  const elevationCache = useRef({});

  // Auto-pick a sensible default attribute per layer once data (or its
  // schema) changes — preferring a "status"-like field if one exists.
  const pickDefaultAttribute = (fields, layerName) => {
    // Exact "status" field always wins if the layer has one.
    const statusField = fields.find((f) => f.toLowerCase() === "status");
    if (statusField) return statusField;

    // Wards defaults to subcounty specifically.
    if (layerName === "Wards") {
      const subcountyField = fields.find((f) => f.toLowerCase() === "subcounty");
      if (subcountyField) return subcountyField;
    }

    // Otherwise fall back to anything status-like, then the first available field.
    return fields.find((f) => /status/i.test(f)) || fields[0];
  };

  useEffect(() => {
    const fetchData = async () => {
      // Sends the auth token when one exists so the backend can decide
      // whether to include account_name/phone (see optionalAuth on the
      // customer-accounts routes) — anonymous requests simply omit the
      // header and get the public field set back.
      //
      // ASSUMPTION: token lives in localStorage under "token". Swap this
      // for however this app actually stores it (a different key, a
      // cookie the browser sends automatically, an auth context, etc.).
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const requests = Object.entries(layers).map(([key, layer]) =>
        fetch(layer.url, { headers: authHeaders }).then((res) =>
          res.ok ? res.json() : Promise.reject(`Failed: ${key}`)
        )
      );

      const responses = await Promise.allSettled(requests);
      const updatedLayers = { ...layers };

      Object.keys(layers).forEach((key, i) => {
        if (responses[i].status === "fulfilled") {
          const data = responses[i].value.data;
          const schema = getLayerSchema(data);
          const fields = schema.map((s) => s.field);

          updatedLayers[key] = {
            ...updatedLayers[key],
            data,
            activeAttribute: pickDefaultAttribute(fields, key) || null,
          };
          setIsLoading(false);
        } else {
          console.error(`Error fetching ${key}:`, responses[i].reason);
        }
      });

      setLayers(updatedLayers);
    };

    fetchData();
  }, []);


  // Schema (available attributes) per layer — recomputed only when the
  // underlying data changes.
  const layerSchemas = useMemo(() => {
    const result = {};
    Object.entries(layers).forEach(([name, cfg]) => {
      result[name] = getLayerSchema(cfg.data || []);
    });

    // getLayerSchema (attributeAnalyzer.js) appears to drop "walkRoute" from
    // Customer Meters — most likely a cardinality heuristic treating a field
    // with this many distinct values as unsuitable for a legend. It's
    // explicitly wanted here ("accounts should also be viewable by the walk
    // route"), so force an entry in just so it appears in the "By:"
    // dropdown and can be selected as `activeAttribute`.
    //
    // Deliberately NOT cloned from another field's schema entry: an earlier
    // version of this did `{ ...someOtherField, field: "walkRoute" }`, which
    // carried that other field's own classification metadata (its fixed
    // enum/color list) along with it — buildClassification then classified
    // walkRoute's values using connectionType's categories, so the legend
    // showed "Domestic/Commercial/..." swatches under "By: Walk Route". The
    // classification itself is now built separately and manually below,
    // bypassing buildClassification/fieldInfo entirely for this field, so
    // this entry only needs to be enough for the dropdown to render it.
    //
    // TODO: this is a stopgap. The real fix belongs in attributeAnalyzer.js,
    // by exempting "walkRoute" from whatever threshold is excluding it.
    const meterSchema = result["Customer Meters"];
    if (meterSchema && !meterSchema.some((s) => s.field === "walkRoute")) {
      result["Customer Meters"] = [...meterSchema, { field: "walkRoute" }];
    }

    return result;
  }, [layers]);

  // Builds a legend classification the same shape LegendLayer expects
  // ({ key, label, color, test(value) }) directly from a layer's own data,
  // for fields where we can't trust attributeAnalyzer.js's buildClassification
  // (see the walkRoute note above). Null/blank values collapse into one
  // "Unspecified" bucket instead of one entry per missing value.
  const buildManualClassification = (data, field) => {
    const values = new Set();
    let hasBlank = false;
    (data || []).forEach((item) => {
      const value = item[field];
      if (value === null || value === undefined || value === "") hasBlank = true;
      else values.add(String(value));
    });

    const sorted = Array.from(values).sort((a, b) => a.localeCompare(b));
    const classes = sorted.map((value, i) => ({
      key: value,
      label: value,
      color: CATEGORICAL[i % CATEGORICAL.length],
      test: (v) => v != null && String(v) === value,
    }));

    if (hasBlank) {
      classes.push({
        key: "__unspecified__",
        label: "Unspecified",
        color: "#b7b4ad",
        test: (v) => v === null || v === undefined || v === "",
      });
    }

    return classes;
  };

  // Legend classes (swatches/filters) for whichever attribute is active
  // per layer right now.
  const layerClassifications = useMemo(() => {
    const result = {};
    Object.entries(layers).forEach(([name, cfg]) => {
      if (cfg.activeAttribute === SHOW_ALL_KEY) {
        result[name] = applyClassColorOverrides([getShowAllClass(name)]);
        return;
      }
      if (name === "Customer Meters" && cfg.activeAttribute === "walkRoute") {
        result[name] = applyClassColorOverrides(buildManualClassification(cfg.data, "walkRoute"));
        return;
      }
      const schema = layerSchemas[name] || [];
      const fieldInfo = schema.find((s) => s.field === cfg.activeAttribute);
      result[name] = applyClassColorOverrides(
        fieldInfo ? buildClassification(cfg.data, cfg.activeAttribute, fieldInfo) : []
      );
    });
    return result;
  }, [layers, layerSchemas]);

  // Returns exactly the items of a layer that are actually showing on the
  // map right now — same rule the map-rendering effect uses to decide
  // whether a feature gets drawn: it has to belong to a class (under the
  // layer's current "By: <attribute>" grouping) that hasn't been unchecked
  // in the legend. A layer that's toggled off in the legend counts as
  // showing nothing. This is what makes Insights reactive to both the
  // search bar (which already filters `cfg.data` itself) and the legend's
  // per-class checkboxes (which don't touch `cfg.data`, only what's drawn).
  const getVisibleLayerData = (layerName) => {
    const cfg = layers[layerName];
    if (!cfg || !cfg.data || !cfg.visibility) return [];

    const activeAttribute = cfg.activeAttribute;
    const classes = layerClassifications[layerName] || [];
    if (!activeAttribute || classes.length === 0) return cfg.data;

    const classFilters = cfg.classFilters?.[activeAttribute] || {};
    const isVisible = (key) => (classFilters[key] !== undefined ? classFilters[key] : true);

    return cfg.data.filter((item) => {
      const matched = classes.find((c) => c.test(item[activeAttribute]));
      return matched ? isVisible(matched.key) : false;
    });
  };

  // Whichever layer Insights should drill into: the search bar's scope
  // when it's pinned to one specific layer, otherwise Customer Meters (the
  // primary entity this page is built around).
  const insightsFocusLayer =
    searchScope !== ALL_LAYERS_KEY && layers[searchScope] ? searchScope : "Customer Meters";

  // Everything the Insights panel renders — recomputed on every layers/
  // classification/scope change, so it's never more than a render behind
  // whatever's actually visible on the map.
  const insightsData = useMemo(() => {
    const perLayerVisible = {};
    Object.keys(layers).forEach((name) => {
      perLayerVisible[name] = getVisibleLayerData(name).length;
    });

    const focusData = getVisibleLayerData(insightsFocusLayer);
    const focusAttribute = layers[insightsFocusLayer]?.activeAttribute;

    // Generic "By: <whatever the legend is grouping this layer by right
    // now>" breakdown — works for any layer/attribute, not just the
    // Customer Meters fields this panel used to be hardcoded around.
    let breakdown = [];
    if (focusAttribute && focusAttribute !== SHOW_ALL_KEY) {
      const counts = {};
      focusData.forEach((item) => {
        const raw = item[focusAttribute];
        const key = raw === null || raw === undefined || raw === "" ? "Unspecified" : String(raw);
        counts[key] = (counts[key] || 0) + 1;
      });
      breakdown = Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([label, count]) => ({ label, count }));
    }

    // Customer-Meters-only figures — account status split and capture
    // trend only mean something for this layer's own fields.
    let activeCount = null;
    let otherCount = null;
    let activePct = null;
    let captureTrend = [];

    if (insightsFocusLayer === "Customer Meters") {
      activeCount = focusData.filter((i) => String(i.accountStatus).toLowerCase() === "active").length;
      otherCount = focusData.length - activeCount;
      activePct = focusData.length ? Math.round((activeCount / focusData.length) * 100) : 0;

      const buckets = {};
      focusData.forEach((item) => {
        if (!item.capturedOn) return;
        const d = new Date(item.capturedOn);
        if (Number.isNaN(d.getTime())) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        buckets[key] = (buckets[key] || 0) + 1;
      });
      captureTrend = Object.entries(buckets)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([period, count]) => ({ period, count }));
    }

    return {
      focusLayerName: insightsFocusLayer,
      focusAttribute,
      total: focusData.length,
      breakdown,
      activeCount,
      otherCount,
      activePct,
      captureTrend,
      perLayerVisible,
      scopeIsAllLayers: searchScope === ALL_LAYERS_KEY,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, layerClassifications, insightsFocusLayer, searchScope]);

  const handleAttributeChange = (layerName, field) => {
    setLayers((prev) => ({
      ...prev,
      [layerName]: { ...prev[layerName], activeAttribute: field },
    }));
  };

  const handleToggleClass = (layerName, attribute, classKey) => {
    setLayers((prev) => {
      const cfg = prev[layerName];
      const currentFilters = cfg.classFilters?.[attribute] || {};
      const currentVal = currentFilters[classKey] !== undefined ? currentFilters[classKey] : true;
      return {
        ...prev,
        [layerName]: {
          ...cfg,
          classFilters: {
            ...cfg.classFilters,
            [attribute]: { ...currentFilters, [classKey]: !currentVal },
          },
        },
      };
    });
  };

  useEffect(() => {
    const basemapLayer = new TileLayer({
      title: "Basemaps",
      source: new XYZ({
        url: myData[basemapSelected].url,
        attributions: myData[basemapSelected].attribution,
        crossOrigin: "Anonymous",
      }),
    });

    const initialMap = new Map({
      target: mapElement.current,
      layers: [basemapLayer],
      view: new View({
        projection: "EPSG:3857",
        center: fromLonLat([36.777382, -1.040343]),
        zoom: 12.5,
        maxZoom: 32,
      }),
      controls: defaultControls().extend([
        new ZoomToExtent({
          extent: [
            ...fromLonLat([36.6970, -1.1335]),
            ...fromLonLat([36.8846, -0.9800]),
          ],
        }),
        new ScaleLine({ units: "metric" }),
      ]),
    });

    setMap(initialMap);
    return () => initialMap.setTarget(undefined);
  }, []);

  useEffect(() => {
    if (!map) return;
    const basemapLayer = map.getLayers().getArray().find((layer) => layer.get("title") === "Basemaps");
    if (basemapLayer) {
      const { url, attribution } = myData[basemapSelected];
      basemapLayer.setSource(new XYZ({ url, attributions: attribution, crossOrigin: "Anonymous" }));
      basemapLayer.setVisible(basemapsVisible);
    }
  }, [map, basemapSelected, basemapsVisible]);

  // Looks up ground elevation for a lon/lat pair from the elevation API
  // (never from a feature's own attributes), with a small in-memory cache.
  const fetchElevation = async (lon, lat) => {
    const key = `${lon.toFixed(4)},${lat.toFixed(4)}`;

    if (key in elevationCache.current) {
      return elevationCache.current[key];
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 6000);

    try {
      const url =
        `/api/elevation?lat=${encodeURIComponent(lat)}` +
        `&lon=${encodeURIComponent(lon)}`;

      const res = await fetch(url, {
        signal: controller.signal,
        headers: {
          Accept: "application/json"
        }
      });

      if (!res.ok) {
        throw new Error(`Elevation lookup failed (${res.status})`);
      }

      const json = await res.json();

      if (json.elevation == null) {
        throw new Error("No elevation returned");
      }

      elevationCache.current[key] = json.elevation;

      return json.elevation;

    } catch (err) {
      console.warn(
        "Elevation lookup failed:",
        err?.message || err
      );

      return null;

    } finally {
      clearTimeout(timeout);
    }
  };

  useEffect(() => {
    if (!map) return;

    popupOverlay.current = new Overlay({
      element: popupElement.current,
      autoPan: true,
      positioning: "bottom-center",
      offset: [0, -15],
    });

    map.addOverlay(popupOverlay.current);

    map.on("singleclick", (event) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (feat) => feat);
      const [lon, lat] = toLonLat(event.coordinate);

      let contentHTML;

      if (feature) {
        const properties = feature.getProperties();
        const layerName = feature.get("layerName") || "Feature";
        // Internal bookkeeping we set on every feature (see the layer-build
        // effect below) — never meant for display.
        [
          "geometry", "geom", "uid", "scuid", "cuid", "__gid", "path",
          "featureId", "layerName", "classKey",
        ].forEach((key) => delete properties[key]);
        const entries = Object.entries(properties);
        const [subtitleKey, subtitleValue] = entries[0] || [];

        contentHTML = `
          <div class="popup-header">
            <div class="popup-title">${layerName}</div>
            ${subtitleKey ? `<div class="popup-subtitle">${subtitleKey}: ${subtitleValue}</div>` : ""}
          </div>
          <div class="popup-content-wrapper">
            <div class="popup-elevation-chip">
              <span aria-hidden="true">⛰</span>
              <span id="popup-elevation">Fetching elevation…</span>
            </div>
            <table class="popup-table">
              ${entries
                .map(([key, value]) => `<tr><td class="popup-key">${key}</td><td class="popup-value">${value}</td></tr>`)
                .join("")}
            </table>
            <button class="popup-zoom" id="zoomToFeatureBtn">Zoom to Feature</button>
          </div>
        `;
      } else {
        // No feature under the click — still report where the click landed
        // and what the terrain there is, sourced from the elevation API
        // (not from any point data).
        contentHTML = `
          <div class="popup-header">
            <div class="popup-title">Map location</div>
            <div class="popup-subtitle">${lon.toFixed(5)}, ${lat.toFixed(5)}</div>
          </div>
          <div class="popup-content-wrapper">
            <div class="popup-elevation-chip">
              <span aria-hidden="true">⛰</span>
              <span id="popup-elevation">Fetching elevation…</span>
            </div>
          </div>
        `;
      }

      document.getElementById("popup-content").innerHTML = contentHTML;
      popupOverlay.current.setPosition(event.coordinate);

      if (feature) {
        setTimeout(() => {
          const btn = document.getElementById("zoomToFeatureBtn");
          if (btn) {
            btn.onclick = () => {
              const geometry = feature.getGeometry();
              map.getView().fit(geometry.getExtent(), { maxZoom: 17, duration: 1000 });
            };
          }
        }, 200);
      }

      fetchElevation(lon, lat).then((elevation) => {
        const el = document.getElementById("popup-elevation");
        if (!el) return; // popup moved on before the lookup resolved
        el.textContent = elevation != null ? `${Math.round(elevation)} m a.s.l.` : "Unavailable";
      });
    });
  }, [map]);

  // Fullscreen handling — toggles the whole map wrapper (map + legend +
  // buttons + tabular data), and nudges OL to resize once the browser
  // finishes the transition.
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => map?.updateSize(), 150);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [map]);

  const toggleFullscreen = () => {
    const el = mapWrapperRef.current;
    if (!document.fullscreenElement) {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    }
  };

  useEffect(() => {
    if (!map) return;

    const svgGenerators = {
      "Customer Meters": getColoredMeterSVG,
      "Tanks": getTankSVG,
      "ControlValves": getControlValveSVG,
      "Boreholes": getBoreholeSVG,
      "Weirs": getWeirSVG,
      "T-Works": getTreatmentWorksSVG,
      "Offtakes": getOfftakeSVG,
      "Bulkmeters": getBulkMeterSVG,
    };

    Object.entries(layers).forEach(([layerName, layerConfig]) => {
      let vectorLayer = map.getLayers().getArray().find((l) => l.get("title") === layerName);

      if (!vectorLayer) {
        vectorLayer = new VectorLayer({ title: layerName, source: new VectorSource() });
        map.addLayer(vectorLayer);
      }

      const source = vectorLayer.getSource();
      source.clear();

      const activeAttribute = layerConfig.activeAttribute;
      const classes = layerClassifications[layerName] || [];
      const classFilters = layerConfig.classFilters?.[activeAttribute] || {};
      const isClassVisible = (key) => (classFilters[key] !== undefined ? classFilters[key] : true);

      if (layerConfig.visibility && layerConfig.data && activeAttribute) {
        const styleCache = {};
        const iconGenerator = svgGenerators[layerName];

        vectorLayer.setStyle((feature) => {
          const featureId = feature.get("featureId");
          const featureLayer = feature.get("layerName");
          const classKey = feature.get("classKey");

          const isSelected =
            selectedFeature && featureId === selectedFeature.id && featureLayer === selectedFeature.layerName;

          const baseStyle = styleCache[classKey];
          if (!isSelected) return baseStyle;

          const geomType = feature.getGeometry().getType();

          if (geomType === "Point") {
            return [
              baseStyle,
              new Style({
                image: new CircleStyle({ radius: 12, fill: new Fill({ color: "rgba(255,165,0,0.3)" }) }),
                zIndex: 999,
              }),
            ];
          }
          if (geomType.includes("LineString")) {
            return new Style({ stroke: new Stroke({ color: "orange", width: 4 }), zIndex: 999 });
          }
          if (geomType.includes("Polygon")) {
            return new Style({
              stroke: new Stroke({ color: "orange", width: 2 }),
              fill: new Fill({ color: "rgba(255,165,0,0.3)" }),
              zIndex: 999,
            });
          }
          return baseStyle;
        });

        const features = layerConfig.data
          .filter((item) => item.geom)
          .map((item) => {
            const matchedClass = classes.find((c) => c.test(item[activeAttribute]));
            if (!matchedClass || !isClassVisible(matchedClass.key)) return null;

            let geometry;
            let properties = {};

            if (item.geom.type === "Point") {
              geometry = new Point(fromLonLat(item.geom.coordinates));
              // A fixed, friendly field list for Customer Meters — not the
              // raw activeAttribute spread, which (for this layer) is
              // almost always "meterStatus" and would otherwise duplicate
              // "Meter Status" below under a different label.
              properties =
                layerName === "Customer Meters"
                  ? compact({
                      "Account No.": item.accountNo,
                      // Account Name / Phone are only present in the
                      // payload at all when the request was authenticated
                      // (see optionalAuth on the backend) — compact()
                      // below just drops them from the popup when absent
                      // rather than showing "undefined".
                      "Account Name": item.accountName,
                      "Phone": item.phone,
                      "Meter Serial": item.meterSerial,
                      "Meter Size": item.meterSize,
                      "Meter Status": item.meterStatus,
                      "Connection Type": item.connectionType,
                      "Walk Route": item.walkRoute,
                      // From the point record itself, as captured in the
                      // field — distinct from the live lookup shown in the
                      // popup's elevation chip, which comes from the
                      // elevation API instead.
                      "Elevation (recorded)": item.elevation != null ? `${item.elevation} m` : undefined,
                      "Date Captured": formatCapturedOn(item.capturedOn),
                    })
                  : activeAttribute !== SHOW_ALL_KEY
                  ? { [activeAttribute]: item[activeAttribute] }
                  : {};
            } else if (item.geom.type === "MultiLineString") {
              geometry = new MultiLineString(
                item.geom.coordinates.map((line) => line.map(([lon, lat]) => fromLonLat([lon, lat])))
              );
              properties = { ...item };
            } else if (item.geom.type === "MultiPolygon") {
              geometry = new MultiPolygon(
                item.geom.coordinates.map((polygon) =>
                  polygon.map((ring) => ring.map(([lon, lat]) => fromLonLat([lon, lat])))
                )
              );
              properties = { ...item };
            }

            if (!geometry) return null;

            const feature = new Feature({ geometry, ...properties });
            const id = item.id || item.fid || item.gid;

            feature.set("featureId", id);
            feature.set("layerName", layerName);
            feature.set("classKey", matchedClass.key);

            if (!styleCache[matchedClass.key]) {
              if (geometry.getType() === "Point") {
                if (iconGenerator) {
                  styleCache[matchedClass.key] = new Style({
                    image: new Icon({
                      src: iconGenerator(matchedClass.color),
                      anchor: [0.5, 0.5],
                      scale: 0.6,
                      crossOrigin: "anonymous",
                    }),
                  });
                } else {
                  styleCache[matchedClass.key] = new Style({
                    image: new CircleStyle({
                      radius: 6,
                      fill: new Fill({ color: `${matchedClass.color}` }),
                      stroke: new Stroke({ color: "#fff", width: 1 }),
                    }),
                  });
                }
              } else if (geometry.getType().includes("LineString")) {
                styleCache[matchedClass.key] = new Style({
                  stroke: new Stroke({ color: matchedClass.color, width: 1.5 }),
                });
              } else if (geometry.getType().includes("Polygon")) {
                styleCache[matchedClass.key] = new Style({
                  stroke: new Stroke({ color: matchedClass.color, width: 0.9 }),
                  fill: new Fill({ color: `${matchedClass.color}33` }),
                });
              }
            }

            return feature;
          });

        source.addFeatures(features.filter(Boolean));
      }

      vectorLayer.setVisible(layerConfig.visibility);
    });
  }, [map, layers, selectedFeature, layerClassifications]);

  const exportMap = () => {
    if (!map) return;
    const title = prompt("Enter a title for the map:", "My Map Title");
    if (title === null) return;

    map.once("rendercomplete", () => {
      const mapCanvas = map.getViewport().querySelector("canvas");
      if (!mapCanvas) return;

      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = mapCanvas.width;
      exportCanvas.height = mapCanvas.height;
      const ctx = exportCanvas.getContext("2d");
      ctx.drawImage(mapCanvas, 0, 0);

      const legend = document.querySelector(".legend");
      const northArrow = new Image();
      northArrow.src = northarrow;

      northArrow.onload = () => {
        ctx.drawImage(northArrow, exportCanvas.width - 250, 160, 245, 260);

        ctx.fillStyle = "black";
        ctx.fillRect(exportCanvas.width - 180, exportCanvas.height - 60, 100, 6);

        ctx.fillStyle = "white";
        ctx.font = "14px sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillText("100m", exportCanvas.width - 170, exportCanvas.height - 65);

        const titleFontSize = 50;
        ctx.font = `bold ${titleFontSize}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";

        const textWidth = ctx.measureText(title).width;
        const textX = exportCanvas.width / 2;
        const textY = 30;
        const padding = 40;

        ctx.fillStyle = "white";
        ctx.fillRect(textX - textWidth / 2 - padding / 2, textY - padding / 2, textWidth + padding, titleFontSize + padding);

        ctx.fillStyle = "black";
        ctx.font = `bold ${titleFontSize}px Arial`;
        ctx.fillText(title, textX, textY);

        ctx.font = "italic 18px Arial";
        ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillText("© Githunguri Water and Sanitation Company Ltd.", 20, exportCanvas.height - 20);

        if (legend) {
          html2canvas(legend).then((legendCanvas) => {
            const legendX = exportCanvas.width - legendCanvas.width - 30;
            const legendY = exportCanvas.height - legendCanvas.height - 30;
            ctx.drawImage(legendCanvas, legendX, legendY);
            const link = document.createElement("a");
            link.download = "map_export.png";
            link.href = exportCanvas.toDataURL("image/png");
            link.click();
          });
        } else {
          const link = document.createElement("a");
          link.download = "map_export.png";
          link.href = exportCanvas.toDataURL("image/png");
          link.click();
        }
      };
    });

    map.renderSync();
  };

  const handleRowClick = (row, layerName) => {
    if (!map || !layers[layerName]) return;
    const id = row.id || row.fid || row.gid;
    setSelectedFeature({ layerName, id });

    const item = layers[layerName].data.find((d) => (d.id || d.fid || d.gid) === id);
    if (!item || !item.geom) return;

    let geometry;
    switch (item.geom.type) {
      case "Point":
        geometry = new Point(fromLonLat(item.geom.coordinates));
        break;
      case "MultiLineString":
        geometry = new MultiLineString(item.geom.coordinates.map((line) => line.map(([lon, lat]) => fromLonLat([lon, lat]))));
        break;
      case "MultiPolygon":
        geometry = new MultiPolygon(
          item.geom.coordinates.map((polygon) => polygon.map((ring) => ring.map(([lon, lat]) => fromLonLat([lon, lat]))))
        );
        break;
      default:
        return;
    }

    map.getView().fit(geometry.getExtent(), { padding: [40, 200, 60, 60], duration: 600, maxZoom: 20 });
  };

  return (
    <div className="gis">
      <GISHeader toggleMenu={toggleMenu} setToggleMenu={setToggleMenu} />
      {toggleMenu && <MenuTab toggleMenu={toggleMenu} setToggleMenu={setToggleMenu} />}

      <div className="map" ref={mapWrapperRef}>
        {isLoading && <LoadingWidget />}

        <div className="map_container" ref={mapElement}>
          <div ref={popupElement} className="ol-popup">
            <button className="ol-popup-closer" onClick={() => popupOverlay.current.setPosition(undefined)}>
              <FaTimes />
            </button>
            <div id="popup-content"></div>
          </div>
        </div>

        <div className="map-buttons">
          {legendCollapsed && (
            <button onClick={() => setLegendCollapsed(false)}>
              <FaLayerGroup />
              <span className="tooltip">Layers</span>
            </button>
          )}
          <button onClick={() => locateUser(map)}>
            <FaMapMarkerAlt />
            <span className="tooltip">Locate Me</span>
          </button>
          {insightsCollapsed && (
            <button onClick={() => setInsightsCollapsed(false)}>
              <FaChartBar />
              <span className="tooltip">Insights</span>
            </button>
          )}
          <button onClick={() => { setLegendCollapsed(false); exportMap(); }}>
            <FaMap />
            <span className="tooltip">Export Map</span>
          </button>
          <button onClick={toggleFullscreen}>
            {isFullscreen ? <FaCompress /> : <FaExpand />}
            <span className="tooltip">{isFullscreen ? "Exit Fullscreen" : "Fullscreen"}</span>
          </button>
        </div>

        <TabularData layers={layers} setLayers={setLayers} handleRowClick={handleRowClick} />
        <SearchBar
          layers={layers}
          setLayers={setLayers}
          scope={searchScope}
          setScope={setSearchScope}
        />

        {!legendCollapsed && (
          <Legend
            layers={layers}
            setLayers={setLayers}
            layerSchemas={layerSchemas}
            layerClassifications={layerClassifications}
            onAttributeChange={handleAttributeChange}
            onToggleClass={handleToggleClass}
            myData={myData}
            legendCollapsed={legendCollapsed}
            setLegendCollapsed={setLegendCollapsed}
            basemapsVisible={basemapsVisible}
            setBasemapsVisible={setBasemapsVisible}
            basemapSelected={basemapSelected}
            setBasemapSelected={setBasemapSelected}
          />
        )}

        {!insightsCollapsed && (
          <InsightsPanel data={insightsData} isLoading={isLoading} onClose={() => setInsightsCollapsed(true)} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Insights panel — a small read-only dashboard computed entirely from
// `layers` (see insightsData above), so it updates on every search
// keystroke and every legend checkbox — never a separate fetch, never
// stale relative to what's actually drawn on the map.
// ---------------------------------------------------------------------
function InsightsPanel({ data, isLoading, onClose }) {
  const {
    focusLayerName, focusAttribute, total, breakdown,
    activeCount, otherCount, activePct, captureTrend,
    perLayerVisible, scopeIsAllLayers,
  } = data;

  const layerNames = Object.keys(perLayerVisible);
  const grandTotal = Object.values(perLayerVisible).reduce((sum, n) => sum + n, 0);

  if (isLoading && grandTotal === 0) {
    return (
      <div className="insights-panel">
        <PanelHeader onClose={onClose} />
        <p className="insights-empty">Loading…</p>
      </div>
    );
  }

  const topBreakdown = breakdown.slice(0, 6);
  const maxBreakdownCount = Math.max(1, ...topBreakdown.map((r) => r.count));
  const extraBreakdownCount = breakdown.length - topBreakdown.length;

  return (
    <div className="insights-panel">
      <PanelHeader onClose={onClose} />

      {/* Per-layer visible counts — always shown, so switching the search
          bar's scope or toggling a legend checkbox is visible at a glance
          across every layer, not just the one currently drilled into. */}
      <div className="insights-section">
        <div className="insights-section-label">
          Visible features {scopeIsAllLayers ? "(all layers)" : `— scoped to ${focusLayerName}`}
        </div>
        {layerNames.map((name) => (
          <BarRow
            key={name}
            label={name}
            value={perLayerVisible[name]}
            max={Math.max(1, ...layerNames.map((n) => perLayerVisible[n]))}
            color={name === focusLayerName ? SEQUENTIAL_BLUE : "#c7c4bc"}
          />
        ))}
      </div>

      {total === 0 ? (
        <p className="insights-empty">
          No {focusLayerName} features match the current search/legend filters.
        </p>
      ) : (
        <>
          {/* Hero figure — for whichever layer is currently in focus */}
          <div className="insights-hero">
            <div className="insights-hero-label">{focusLayerName} showing now</div>
            <div className="insights-hero-value">{total.toLocaleString()}</div>
          </div>

          {/* Active vs other — Customer Meters only, since accountStatus is
              specific to that layer. */}
          {focusLayerName === "Customer Meters" && (
            <div className="insights-chips">
              <StatChip
                icon={<FaCheckCircle color={STATUS_COLOR.good} size={14} />}
                label="Active"
                value={`${activeCount.toLocaleString()} (${activePct}%)`}
              />
              <StatChip
                icon={<FaTimesCircle color={STATUS_COLOR.critical} size={14} />}
                label="Other"
                value={otherCount.toLocaleString()}
              />
            </div>
          )}

          {/* Generic breakdown by whatever attribute the legend is
              currently grouping this layer by — Walk Route, Meter Status,
              Connection Type, anything selectable in the "By:" dropdown. */}
          {topBreakdown.length > 0 && (
            <div className="insights-section">
              <div className="insights-section-label">
                By {humanizeField(focusAttribute).toLowerCase()}
              </div>
              {topBreakdown.map((row, i) => (
                <BarRow
                  key={row.label}
                  label={row.label}
                  value={row.count}
                  max={maxBreakdownCount}
                  color={CATEGORICAL[i % CATEGORICAL.length]}
                />
              ))}
              {extraBreakdownCount > 0 && (
                <div className="insights-more">+{extraBreakdownCount} more</div>
              )}
            </div>
          )}

          {/* Capture trend sparkline — Customer Meters only. */}
          {focusLayerName === "Customer Meters" && captureTrend.length > 1 && (
            <div className="insights-section insights-section--last">
              <div className="insights-section-label">Capture trend</div>
              <Sparkline data={captureTrend} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PanelHeader({ onClose }) {
  return (
    <div className="insights-header">
      <strong>Insights</strong>
      <button onClick={onClose} aria-label="Close insights" className="insights-close">
        <FaTimes size={13} />
      </button>
    </div>
  );
}

function StatChip({ icon, label, value }) {
  return (
    <div className="insights-chip">
      {icon}
      <div>
        <div className="insights-chip-label">{label}</div>
        <div className="insights-chip-value">{value}</div>
      </div>
    </div>
  );
}

function BarRow({ label, value, max, color }) {
  const pct = Math.max(4, Math.round((value / max) * 100));
  return (
    <div className="insights-bar-row">
      <div className="insights-bar-row-labels">
        <span>{label}</span>
        <span>{value.toLocaleString()}</span>
      </div>
      <div className="insights-bar-track">
        <div className="insights-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// 2px line, rounded caps, no per-point labels — just an end-value read out
// beside the chart, per the marks-and-anatomy spec for a stat-tile trend.
function Sparkline({ data }) {
  const width = 220;
  const height = 40;
  const values = data.map((d) => d.count);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * (width - 4) + 2;
    const y = height - 2 - ((d.count - min) / range) * (height - 4);
    return `${x},${y}`;
  });

  const last = data[data.length - 1];
  const [lastX, lastY] = points[points.length - 1].split(",");

  return (
    <div className="insights-sparkline">
      <svg width={width} height={height} role="img" aria-label="Capture volume over time">
        <polyline
          points={points.join(" ")}
          fill="none"
          stroke={SEQUENTIAL_BLUE}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx={lastX} cy={lastY} r={4} fill={SEQUENTIAL_BLUE} stroke="#fcfcfb" strokeWidth={2} />
      </svg>
      <div className="insights-sparkline-caption">
        {last.count.toLocaleString()} on {last.date}
      </div>
    </div>
  );
}
